// ============================================================================
// FOOTER COMPONENT - Shared footer for all pages
// ============================================================================

import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/**
 * Footer Component
 * @param {'light' | 'dark'} variant - Footer style
 * @param {boolean} showLinks - Show footer links
 * @param {boolean} minimal - Minimal version (just copyright)
 */
const Footer = ({ 
  variant = 'light',
  showLinks = true,
  minimal = false 
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';

  const variants = {
    light: {
      bg: 'bg-gray-50 border-t border-gray-200',
      text: 'text-gray-600',
      textHover: 'hover:text-gray-900',
      copyright: 'text-gray-500'
    },
    dark: {
      bg: 'bg-[#0a0b14] border-t border-gray-800',
      text: 'text-gray-400',
      textHover: 'hover:text-white',
      copyright: 'text-gray-500'
    }
  };

  const style = variants[variant] || variants.light;

  if (minimal) {
    return (
      <footer className={`py-6 ${style.bg}`} dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className={`text-sm ${style.copyright}`}>
            {t('footer.copyright')}
          </p>
        </div>
      </footer>
    );
  }

  return (
    <footer className={`py-12 ${style.bg}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          {/* Logo - Simple Text */}
          <Link to="/" className="font-serif text-2xl font-bold">
            <span className={variant === 'dark' ? 'text-white' : 'text-gray-900'}>Lab</span>
            <span className="text-teal-500">2040</span>
          </Link>

          {/* Links */}
          {showLinks && (
            <div className="flex items-center gap-6">
              <Link 
                to="/privacy" 
                className={`${style.text} ${style.textHover} transition-colors`}
              >
                {t('footer.privacy')}
              </Link>
              <Link 
                to="/terms" 
                className={`${style.text} ${style.textHover} transition-colors`}
              >
                {t('footer.terms')}
              </Link>
              <a 
                href="mailto:support@lab2040.ai" 
                className={`${style.text} ${style.textHover} transition-colors`}
              >
                {t('footer.contact')}
              </a>
            </div>
          )}

          {/* Copyright */}
          <p className={`text-sm ${style.copyright}`}>
            {t('footer.copyright')}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
